import React, { useContext, useCallback } from 'react';
import defaultStyles from './pageCta.module.scss';
import { Button } from '@xo-union/tk-component-buttons';
import { Subhead } from '@xo-union/tk-ui-typography';
import Honeybadger from 'honeybadger-js';
import { pagesQuery } from '../../LeftContainer/graphql';
import { TrackingContext } from '../../../TrackingContext';
import {
  useCreatePageMutation,
  Wws_AllowedCreatePageType,
  Wws_PageAttributes,
  PagesQuery,
  PagesFragment,
} from '../../../generated/graphql';

interface PageCTAProps {
  subheadText: string;
  buttonText: string;
  onClick?: () => void;
  disabled?: boolean;
  dataTestIdPrefix?: string;
  wrapperClassName?: string;
  subheadClassName?: string;
  buttonClassName?: string;
  // When present, a createPage mutation is executed instead of delegating to onClick
  pageAttributes?: {
    title: string;
    description?: string;
    show?: boolean;
    rank?: number;
    type?: Wws_AllowedCreatePageType;
  };
  trackEventName?: string;
  // Full page object for parent side-effects (e.g. iframe message)
  onCreatedPage?: (page: PagesFragment) => void;
}

export const PageCTA: React.FC<PageCTAProps> = ({
  subheadText,
  buttonText,
  onClick,
  disabled = false,
  dataTestIdPrefix = 'create-page-cta',
  wrapperClassName,
  subheadClassName,
  buttonClassName,
  pageAttributes,
  trackEventName = 'WWS Page Added',
  onCreatedPage,
}) => {
  const { track } = useContext(TrackingContext);
  const [createPage, { loading }] = useCreatePageMutation({
    refetchQueries: [{ query: pagesQuery }],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      if (!data?.createPage) return;
      try {
        const { pages } = cache.readQuery<PagesQuery>({ query: pagesQuery })!;
        cache.writeQuery({
          query: pagesQuery,
          data: { pages: [...pages, data.createPage] },
        });
      } catch (err) {
        Honeybadger.notify(err, {
          context: { component: 'PageCTA', action: 'apollo-cache-update' },
        });
      }
    },
  });

  const handleClick = useCallback(async () => {
    if (loading || disabled) return;
    if (!pageAttributes) {
      onClick?.();
      return;
    }
    try {
      const { title, description, show, rank, type } = pageAttributes;
      const attrs: Wws_PageAttributes = { title, description, show, rank, type };
      const { data } = await createPage({ variables: { attributes: attrs } });
      const createdPage = data?.createPage;

      track(trackEventName, {
        action: 'add',
        pageTitle: createdPage?.title || title,
        pageVisible: createdPage?.show ?? show ?? true,
        pageType: createdPage?.type ?? type,
        page: createdPage?.title || title,
      });

      if (createdPage) onCreatedPage?.(createdPage);
    } catch (err) {
      Honeybadger.notify(err, {
        context: { component: 'PageCTA', action: 'create-page-mutation' },
      });
    }
  }, [loading, disabled, pageAttributes, onClick, createPage, track, trackEventName, onCreatedPage]);

  return (
    <div data-testid={dataTestIdPrefix} className={`${defaultStyles.pageCtaWrapper} ${wrapperClassName || ''}`}>
      <Subhead as="h3" className={`${defaultStyles.pageCtaSubhead} ${subheadClassName || ''}`}>
        {subheadText}
      </Subhead>
      <Button
        size="sm"
        onClick={handleClick}
        className={`${defaultStyles.pageCtaButton} ${buttonClassName || ''}`}
        color="secondary-alternative"
        data-testid={`${dataTestIdPrefix}-btn`}
        disabled={disabled || loading}
        aria-busy={loading}
      >
        {buttonText}
      </Button>
    </div>
  );
};

export default PageCTA;
